import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Stamp, 
  Settings, 
  Play, 
  CheckCircle, 
  XCircle, 
  Clock,
  Printer,
  Search,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import api from '@/services/api';
import { useToast } from '@/hooks/useToast';

interface MarkingStats {
  total: number;
  marked: number;
  unmarked: number;
  percentMarked: number;
  pendingJobs: number;
}

interface UnmarkedAsset {
  id: string;
  assetTag: string;
  name: string;
  serialNumber?: string;
  category?: { name: string };
  branch?: { name: string };
}

interface MarkingMethod {
  id: string;
  name: string;
  description: string;
}

interface EngravingMachine {
  id: string;
  name: string;
  model?: string;
  connectionType: string;
  isOnline: boolean;
  status: string;
}

interface EngravingJob {
  id: string;
  assetId: string;
  markingText: string;
  markingMethod: string;
  status: string;
  queuedAt: string;
  completedAt?: string;
}

export default function MarkingPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  
  const [selectedAssets, setSelectedAssets] = useState<string[]>([]);
  const [markingMethod, setMarkingMethod] = useState('');
  const [selectedMachine, setSelectedMachine] = useState('');
  const [showMarkModal, setShowMarkModal] = useState(false);
  const [markingText, setMarkingText] = useState('');
  const [markingLocation, setMarkingLocation] = useState('');
  const [currentAssetId, setCurrentAssetId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Fetch statistics
  const { data: stats } = useQuery<MarkingStats>({
    queryKey: ['marking-stats'],
    queryFn: async () => {
      const response = await api.get('/marking/stats');
      return response.data.data;
    },
  });

  // Fetch unmarked assets
  const { data: unmarkedAssets, isLoading: loadingAssets } = useQuery<UnmarkedAsset[]>({
    queryKey: ['unmarked-assets'],
    queryFn: async () => {
      const response = await api.get('/marking/unmarked-assets');
      return response.data.data || [];
    },
  });

  // Fetch marking methods
  const { data: methods } = useQuery<MarkingMethod[]>({
    queryKey: ['marking-methods'],
    queryFn: async () => {
      const response = await api.get('/marking/methods');
      return response.data.data || [];
    },
  });

  // Fetch machines
  const { data: machines } = useQuery<EngravingMachine[]>({
    queryKey: ['engraving-machines'],
    queryFn: async () => {
      const response = await api.get('/marking/machines');
      return response.data.data || [];
    },
  });

  // Fetch jobs
  const { data: jobs } = useQuery<EngravingJob[]>({
    queryKey: ['engraving-jobs'],
    queryFn: async () => {
      const response = await api.get('/marking/jobs');
      return response.data.data || [];
    },
  });

  // Mark asset mutation
  const markMutation = useMutation({
    mutationFn: async (data: { assetId: string; markingMethod: string; markingText: string; markingLocation?: string }) => {
      const response = await api.post('/marking/mark', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['marking-stats'] });
      queryClient.invalidateQueries({ queryKey: ['unmarked-assets'] });
      setShowMarkModal(false);
      setMarkingText('');
      setMarkingLocation('');
      setCurrentAssetId('');
      toast({ title: 'Success', description: 'Asset marked successfully' });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.response?.data?.error || 'Failed to mark asset', variant: 'destructive' });
    },
  });

  // Create bulk jobs mutation
  const bulkJobMutation = useMutation({
    mutationFn: async (data: { assetIds: string[]; machineId: string; markingMethod: string }) => {
      const response = await api.post('/marking/jobs/bulk', data);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['engraving-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['unmarked-assets'] });
      setSelectedAssets([]);
      toast({ title: 'Success', description: `Created ${data.data.length} engraving jobs` });
    },
    onError: (error: any) => {
      toast({ title: 'Error', description: error.response?.data?.error || 'Failed to create jobs', variant: 'destructive' });
    },
  });

  // Filter unmarked assets
  const filteredAssets = (unmarkedAssets || []).filter(asset => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      asset.assetTag.toLowerCase().includes(search) ||
      asset.name?.toLowerCase().includes(search) ||
      asset.serialNumber?.toLowerCase().includes(search)
    );
  });

  const handleMarkAsset = (assetId: string) => {
    setCurrentAssetId(assetId);
    // Find the asset from the list and use its tag as default
    const asset = (unmarkedAssets || []).find(a => a.id === assetId);
    if (asset) {
      setMarkingText(asset.assetTag);
    }
    setShowMarkModal(true);
  };

  const handleBulkEngraving = () => {
    if (selectedAssets.length === 0 || !selectedMachine || !markingMethod) {
      toast({ title: 'Error', description: 'Select assets, machine, and method', variant: 'destructive' });
      return;
    }
    bulkJobMutation.mutate({
      assetIds: selectedAssets,
      machineId: selectedMachine,
      markingMethod,
    });
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'FAILED': return <XCircle className="w-4 h-4 text-red-500" />;
      case 'IN_PROGRESS': return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
      default: return <Clock className="w-4 h-4 text-yellow-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Stamp className="w-8 h-8" />
            Asset Marking & Engraving
          </h1>
          <p className="text-muted-foreground">Mark, engrave, and label your assets</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Assets</p>
                <p className="text-2xl font-bold">{stats?.total || 0}</p>
              </div>
              <Stamp className="w-8 h-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Marked</p>
                <p className="text-2xl font-bold text-green-600">{stats?.marked || 0}</p>
              </div>
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Unmarked</p>
                <p className="text-2xl font-bold text-yellow-600">{stats?.unmarked || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Jobs</p>
                <p className="text-2xl font-bold text-blue-600">{stats?.pendingJobs || 0}</p>
              </div>
              <Printer className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Unmarked Assets */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Unmarked Assets</CardTitle>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search..."
                      className="pl-10 w-48"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Bulk actions */}
              {selectedAssets.length > 0 && (
                <div className="mb-4 p-4 bg-muted rounded-lg">
                  <div className="flex flex-wrap items-center gap-4">
                    <span className="text-sm font-medium">{selectedAssets.length} selected</span>
                    <select
                      className="h-9 px-3 rounded-md border border-input bg-background text-sm"
                      value={markingMethod}
                      onChange={(e) => setMarkingMethod(e.target.value)}
                    >
                      <option value="">Select Method</option>
                      {(methods || []).map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                    <select
                      className="h-9 px-3 rounded-md border border-input bg-background text-sm"
                      value={selectedMachine}
                      onChange={(e) => setSelectedMachine(e.target.value)}
                    >
                      <option value="">Select Machine</option>
                      {(machines || []).filter(m => m.isOnline).map(m => (
                        <option key={m.id} value={m.id}>{m.name}</option>
                      ))}
                    </select>
                    <Button 
                      size="sm" 
                      onClick={handleBulkEngraving}
                      disabled={bulkJobMutation.isPending}
                    >
                      {bulkJobMutation.isPending ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Play className="w-4 h-4 mr-2" />
                      )}
                      Send to Machine
                    </Button>
                  </div>
                </div>
              )}

              {loadingAssets ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              ) : filteredAssets.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  {searchTerm ? 'No assets match your search' : 'All assets are marked!'}
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {filteredAssets.map(asset => (
                    <div 
                      key={asset.id} 
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          className="w-4 h-4 rounded border-input"
                          checked={selectedAssets.includes(asset.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedAssets([...selectedAssets, asset.id]);
                            } else {
                              setSelectedAssets(selectedAssets.filter(id => id !== asset.id));
                            }
                          }}
                        />
                        <div>
                          <p className="font-medium">{asset.assetTag}</p>
                          <p className="text-sm text-muted-foreground">
                            {asset.name} {asset.serialNumber && `• S/N: ${asset.serialNumber}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground">
                          {asset.category?.name} • {asset.branch?.name}
                        </span>
                        <Button size="sm" variant="outline" onClick={() => handleMarkAsset(asset.id)}>
                          <Stamp className="w-4 h-4 mr-1" />
                          Mark
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Engraving Machines */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Engraving Machines</CardTitle>
                <Button size="sm" variant="ghost">
                  <Settings className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {(machines || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No machines configured</p>
              ) : (
                <div className="space-y-2">
                  {(machines || []).map(machine => (
                    <div key={machine.id} className="flex items-center justify-between p-2 border rounded">
                      <div>
                        <p className="font-medium text-sm">{machine.name}</p>
                        <p className="text-xs text-muted-foreground">{machine.connectionType}</p>
                      </div>
                      <div className={`w-3 h-3 rounded-full ${machine.isOnline ? 'bg-green-500' : 'bg-gray-300'}`} />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Jobs */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Recent Jobs</CardTitle>
                <Button size="sm" variant="ghost" onClick={() => queryClient.invalidateQueries({ queryKey: ['engraving-jobs'] })}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {(jobs || []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No recent jobs</p>
              ) : (
                <div className="space-y-2">
                  {(jobs || []).slice(0, 5).map(job => (
                    <div key={job.id} className="flex items-center justify-between p-2 border rounded">
                      <div className="flex items-center gap-2">
                        {getStatusIcon(job.status)}
                        <div>
                          <p className="font-medium text-sm">{job.markingText}</p>
                          <p className="text-xs text-muted-foreground">{job.markingMethod}</p>
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground">{job.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Mark Modal */}
      {showMarkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Mark Asset</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Marking Method</Label>
                <select
                  className="w-full h-10 px-3 mt-1 rounded-md border border-input bg-background text-sm"
                  value={markingMethod}
                  onChange={(e) => setMarkingMethod(e.target.value)}
                >
                  <option value="">Select method...</option>
                  {(methods || []).map(m => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label>Marking Text/ID</Label>
                <p className="text-xs text-muted-foreground mb-1">
                  Default is the asset tag. You can add additional text (e.g., company name).
                </p>
                <Input
                  value={markingText}
                  onChange={(e) => setMarkingText(e.target.value)}
                  placeholder="e.g., SAZ001 or ACME-SAZ001"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Additional Text (Optional)</Label>
                <Input
                  placeholder="e.g., Property of ACME Corp"
                  className="mt-1"
                  onChange={(e) => {
                    if (e.target.value) {
                      setMarkingText(prev => prev.includes(' - ') ? prev : `${prev} - ${e.target.value}`);
                    }
                  }}
                />
              </div>
              <div>
                <Label>Location on Asset</Label>
                <Input
                  value={markingLocation}
                  onChange={(e) => setMarkingLocation(e.target.value)}
                  placeholder="e.g., Bottom panel, Back cover"
                  className="mt-1"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowMarkModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={() => markMutation.mutate({
                    assetId: currentAssetId,
                    markingMethod,
                    markingText,
                    markingLocation,
                  })}
                  disabled={!markingMethod || !markingText || markMutation.isPending}
                >
                  {markMutation.isPending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Stamp className="w-4 h-4 mr-2" />
                  )}
                  Mark Asset
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
